import React from 'react';
import './TemplateStylePreview.css';

export const DESIGN_STYLE_OPTIONS = [
  { id: 'All', label: 'All Design Styles' },
  { id: 'modern-corporate', label: 'Modern Corporate' },
  { id: 'minimal-clean', label: 'Minimal Clean' },
  { id: 'bold-promotional', label: 'Bold Promotional' },
  { id: 'editorial-premium', label: 'Editorial Premium' }
];

export const LAYOUT_STYLE_OPTIONS = [
  { id: 'All', label: 'All Layouts' },
  { id: 'hero-cta', label: 'Hero + CTA' },
  { id: 'two-column', label: 'Two Column' },
  { id: 'single-column-story', label: 'Single Column Story' },
  { id: 'card-sections', label: 'Card Sections' }
];

export const DESIGN_PRESETS = {
  'modern-corporate': {
    shell: '#f4f7fb',
    paper: '#ffffff',
    frame: '#d9e2ef',
    hero: '#0f6cbd',
    accent: '#58a6ff',
    text: '#16314d',
    muted: '#6b7f95',
    button: '#0f6cbd',
    buttonText: '#ffffff'
  },
  'minimal-clean': {
    shell: '#f6f4ef',
    paper: '#fffdf8',
    frame: '#e3ddd1',
    hero: '#5f6b5b',
    accent: '#d9c7a3',
    text: '#263127',
    muted: '#7b7f72',
    button: '#5f6b5b',
    buttonText: '#ffffff'
  },
  'bold-promotional': {
    shell: '#fff4ef',
    paper: '#ffffff',
    frame: '#ffd1bf',
    hero: '#c2410c',
    accent: '#fb7185',
    text: '#4a1d0f',
    muted: '#8b5b49',
    button: '#e11d48',
    buttonText: '#ffffff'
  },
  'editorial-premium': {
    shell: '#f3f1ee',
    paper: '#fffdfa',
    frame: '#ddd6cf',
    hero: '#1f2937',
    accent: '#c8a97e',
    text: '#1f1f1f',
    muted: '#6f6a63',
    button: '#1f2937',
    buttonText: '#ffffff'
  }
};

const TEMPLATE_STYLE_MAP = {
  'modern-clean': {
    designStyle: 'minimal-clean',
    layoutStyle: 'single-column-story',
    eyebrow: 'Single Column',
    headline: 'Calm narrative scaffold with clear hierarchy',
    body: 'Built for adaptable longform copy with one direct action.',
    cta: 'Use Layout',
    aiType: 'announcement'
  },
  corporate: {
    designStyle: 'modern-corporate',
    layoutStyle: 'two-column',
    eyebrow: 'Split Content',
    headline: 'Primary column plus supporting side panel',
    body: 'Great when you need a main message with secondary context.',
    cta: 'Use Layout',
    aiType: 'announcement'
  },
  newsletter: {
    designStyle: 'editorial-premium',
    layoutStyle: 'card-sections',
    eyebrow: 'Editorial Blocks',
    headline: 'Feature lead followed by modular scan cards',
    body: 'Designed for rhythmic sections and easy inbox skimming.',
    cta: 'Use Layout',
    aiType: 'newsletter'
  },
  promotional: {
    designStyle: 'bold-promotional',
    layoutStyle: 'hero-cta',
    eyebrow: 'Hero Focus',
    headline: 'High-contrast hero with one dominant CTA',
    body: 'Strong visual weight at top with compact supporting sections.',
    cta: 'Use Layout',
    aiType: 'promotional'
  },
  'event-invite': {
    designStyle: 'editorial-premium',
    layoutStyle: 'hero-cta',
    eyebrow: 'Image Lead Split',
    headline: 'Top image plus detail rail and response row',
    body: 'Useful for action prompts that need quick decision buttons.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'product-update': {
    designStyle: 'modern-corporate',
    layoutStyle: 'card-sections',
    eyebrow: 'Feature Stack',
    headline: 'Stacked media rows for modular storytelling',
    body: 'Balanced blocks that can showcase features, services, or steps.',
    cta: 'Use Layout',
    aiType: 'product-update'
  },
  announcement: {
    designStyle: 'minimal-clean',
    layoutStyle: 'single-column-story',
    eyebrow: 'Notice Narrative',
    headline: 'Banner plus narrative body and detail panel',
    body: 'A text-forward structure with deliberate pacing and clarity.',
    cta: 'Use Layout',
    aiType: 'announcement'
  },
  ecommerce: {
    designStyle: 'bold-promotional',
    layoutStyle: 'two-column',
    eyebrow: 'Media List',
    headline: 'Repeatable image-and-copy rows with banner close',
    body: 'Ideal for catalog-like blocks that need strong scanning behavior.',
    cta: 'Use Layout',
    aiType: 'promotional'
  },
  'zig-zag-story': {
    designStyle: 'modern-corporate',
    layoutStyle: 'two-column',
    eyebrow: 'Zig Zag Story',
    headline: 'Alternating media rows with narrative pacing',
    body: 'A rhythm-first structure for longform messages and case studies.',
    cta: 'Use Layout',
    aiType: 'newsletter'
  },
  'three-card-grid': {
    designStyle: 'editorial-premium',
    layoutStyle: 'card-sections',
    eyebrow: 'Three Card Grid',
    headline: 'Three equal cards plus a unified close',
    body: 'Great for comparisons, options, or parallel content blocks.',
    cta: 'Use Layout',
    aiType: 'newsletter'
  },
  'minimal-text-only': {
    designStyle: 'minimal-clean',
    layoutStyle: 'single-column-story',
    eyebrow: 'Minimal Text',
    headline: 'No-image scaffold optimized for readability',
    body: 'Designed for concise communication and clean accessibility.',
    cta: 'Use Layout',
    aiType: 'announcement'
  },
  'image-quote-focus': {
    designStyle: 'editorial-premium',
    layoutStyle: 'hero-cta',
    eyebrow: 'Image Quote',
    headline: 'Hero image paired with oversized quote panel',
    body: 'A high-emotion composition with clear supporting follow-through.',
    cta: 'Use Layout',
    aiType: 'promotional'
  },
  'cinematic-hero': {
    designStyle: 'bold-promotional',
    layoutStyle: 'hero-cta',
    eyebrow: 'Cinematic Hero',
    headline: 'Full-bleed background header with layered copy',
    body: 'Best for dramatic launches, keynote invites, or premium announcements.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'glassmorphism-showcase': {
    designStyle: 'editorial-premium',
    layoutStyle: 'card-sections',
    eyebrow: 'Glassmorphism',
    headline: 'Translucent content cards over visual backdrop',
    body: 'Great for modern product stories and immersive campaign updates.',
    cta: 'Use Layout',
    aiType: 'product-update'
  },
  'parallax-postcard': {
    designStyle: 'modern-corporate',
    layoutStyle: 'two-column',
    eyebrow: 'Postcard Split',
    headline: 'Image-led postcard with details and action panel',
    body: 'Useful for destination announcements and event itineraries.',
    cta: 'Use Layout',
    aiType: 'newsletter'
  },
  'event-timeline-spotlight': {
    designStyle: 'modern-corporate',
    layoutStyle: 'hero-cta',
    eyebrow: 'Event Timeline',
    headline: 'Hero image invite with schedule spotlight panel',
    body: 'Designed for event invites that need clear date, place, and agenda cues.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'event-vip-launch': {
    designStyle: 'bold-promotional',
    layoutStyle: 'hero-cta',
    eyebrow: 'VIP Launch',
    headline: 'Premium launch invite with benefit cards and dual CTAs',
    body: 'Built for exclusive launch events with high-conversion response actions.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'event-speaker-showcase': {
    designStyle: 'editorial-premium',
    layoutStyle: 'card-sections',
    eyebrow: 'Speaker Showcase',
    headline: 'Speaker-led event invite with featured presenter cards',
    body: 'Great for forums and panels where speaker identity drives registrations.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'event-countdown-reminder': {
    designStyle: 'bold-promotional',
    layoutStyle: 'hero-cta',
    eyebrow: 'Countdown Reminder',
    headline: 'Pre-event reminder with countdown blocks and quick join CTA',
    body: 'Designed for day-before reminders that improve attendance rates.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  },
  'event-workshop-track': {
    designStyle: 'modern-corporate',
    layoutStyle: 'card-sections',
    eyebrow: 'Workshop Tracks',
    headline: 'Track-selection invite with concise agenda cards',
    body: 'Best for workshops where users pick between parallel session options.',
    cta: 'Use Layout',
    aiType: 'event-invite'
  }
};

export const getTemplateStyle = (template) => {
  const mapped = TEMPLATE_STYLE_MAP[template?.id] || {};
  return {
    designStyle: mapped.designStyle || 'modern-corporate',
    layoutStyle: mapped.layoutStyle || 'hero-cta',
    eyebrow: mapped.eyebrow || template?.category || 'Template',
    headline: mapped.headline || template?.name || 'Email Template',
    body: mapped.body || template?.description || 'A polished starting point.',
    cta: mapped.cta || 'Explore',
    aiType: mapped.aiType || 'announcement'
  };
};

export const getAiPresetForTemplate = (template) => {
  const style = getTemplateStyle(template);
  return {
    designStyle: style.designStyle,
    layoutStyle: style.layoutStyle,
    type: style.aiType,
    styleProfile: {
      sourceTemplateId: template?.id || null,
      sourceTemplateName: template?.name || null,
      styleName: style.headline,
      visualMood: style.eyebrow,
      ctaPreference: style.cta,
      ctaTone: style.designStyle === 'bold-promotional' ? 'assertive' : style.designStyle === 'editorial-premium' ? 'refined' : 'professional',
      sectionRhythm: style.layoutStyle === 'card-sections' ? 'modular' : style.layoutStyle === 'single-column-story' ? 'narrative' : style.layoutStyle === 'two-column' ? 'split-focus' : 'hero-led',
      spacingScale: style.layoutStyle === 'single-column-story' ? 'airy' : style.layoutStyle === 'card-sections' ? 'balanced' : 'compact',
      copyDensity: style.layoutStyle === 'hero-cta' ? 'light' : style.layoutStyle === 'single-column-story' ? 'medium' : 'balanced'
    }
  };
};

export const TemplateStylePreview = ({ template, compact = false }) => {
  const preview = getTemplateStyle(template);
  const colors = DESIGN_PRESETS[preview.designStyle] || DESIGN_PRESETS['modern-corporate'];
  const shellClassName = compact ? 'preview-shell compact' : 'preview-shell';

  return (
    <div className={shellClassName} style={{ background: colors.shell }}>
      <div className="preview-paper" style={{ background: colors.paper, borderColor: colors.frame }}>
        <div className="preview-header" style={{ background: colors.hero }}>
          <span className="preview-eyebrow">{preview.eyebrow}</span>
          <div className="preview-header-lines">
            <span />
            <span />
          </div>
        </div>

        {preview.layoutStyle === 'hero-cta' && (
          <div className="preview-layout preview-layout-hero">
            <div className="preview-hero-block" style={{ background: `linear-gradient(135deg, ${colors.hero}, ${colors.accent})` }}>
              <div className="preview-hero-copy">
                <div className="preview-kicker" style={{ color: colors.buttonText }}>Featured</div>
                <div className="preview-title" style={{ color: colors.buttonText }}>{preview.headline}</div>
                <div className="preview-body" style={{ color: 'rgba(255,255,255,0.82)' }}>{preview.body}</div>
                <div className="preview-button" style={{ background: colors.paper, color: colors.hero }}>{preview.cta}</div>
              </div>
            </div>
            <div className="preview-copy-rows">
              <span style={{ background: colors.text }} />
              <span style={{ background: colors.muted }} />
              <span style={{ background: colors.muted }} />
            </div>
          </div>
        )}

        {preview.layoutStyle === 'two-column' && (
          <div className="preview-layout preview-layout-two-column">
            <div className="preview-columns">
              <div className="preview-column-media" style={{ background: `linear-gradient(160deg, ${colors.accent}, ${colors.hero})` }} />
              <div className="preview-column-copy">
                <div className="preview-mini-title" style={{ color: colors.text }}>{preview.headline}</div>
                <div className="preview-copy-rows compact">
                  <span style={{ background: colors.muted }} />
                  <span style={{ background: colors.muted }} />
                  <span style={{ background: colors.muted }} />
                </div>
                <div className="preview-button small" style={{ background: colors.button, color: colors.buttonText }}>{preview.cta}</div>
              </div>
            </div>
          </div>
        )}

        {preview.layoutStyle === 'single-column-story' && (
          <div className="preview-layout preview-layout-story">
            <div className="preview-story-art" style={{ background: `linear-gradient(145deg, ${colors.accent}, ${colors.paper})` }} />
            <div className="preview-story-copy">
              <div className="preview-mini-title centered" style={{ color: colors.text }}>{preview.headline}</div>
              <div className="preview-copy-rows centered">
                <span style={{ background: colors.muted }} />
                <span style={{ background: colors.muted }} />
                <span style={{ background: colors.muted }} />
                <span style={{ background: colors.muted }} />
              </div>
              <div className="preview-button centered" style={{ background: colors.button, color: colors.buttonText }}>{preview.cta}</div>
            </div>
          </div>
        )}

        {preview.layoutStyle === 'card-sections' && (
          <div className="preview-layout preview-layout-cards">
            <div className="preview-banner" style={{ background: `linear-gradient(135deg, ${colors.hero}, ${colors.accent})` }} />
            <div className="preview-card-grid">
              <div className="preview-card-box" style={{ borderColor: colors.frame }}>
                <div className="preview-card-title" style={{ color: colors.text }} />
                <div className="preview-copy-rows compact">
                  <span style={{ background: colors.muted }} />
                  <span style={{ background: colors.muted }} />
                </div>
              </div>
              <div className="preview-card-box accent" style={{ borderColor: colors.frame, background: colors.shell }}>
                <div className="preview-card-media" style={{ background: `linear-gradient(145deg, ${colors.accent}, ${colors.hero})` }} />
              </div>
              <div className="preview-card-box wide" style={{ borderColor: colors.frame }}>
                <div className="preview-mini-title" style={{ color: colors.text }}>{preview.headline}</div>
                <div className="preview-button small" style={{ background: colors.button, color: colors.buttonText }}>{preview.cta}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateStylePreview;